/**
 * product-custom.js — product detail page enhancements (Agent 5)
 *
 * Extends Dawn instead of replacing it:
 *   1. Appends the selected option value to each variant option label ("Color: Sand").
 *   2. Moves the size guide trigger next to the size option and drives its modal
 *      (focus trap via <dialog>, Escape to close, scroll lock, focus restore).
 *   3. Shows the sticky mobile Add to Cart bar once the main button scrolls away.
 *   4. Wires the product tabs (WAI-ARIA tabs on desktop, accordion on mobile).
 *   5. Refreshes design code, low stock, option labels and the sticky bar price
 *      whenever Dawn publishes a variant change.
 *
 * Dawn re-renders the section on variant change and publishes `variant-change`
 * through pubsub.js, so all live regions are refreshed from that response —
 * exactly the mechanism Dawn itself uses for price / SKU / inventory.
 */
(function () {
  'use strict';

  var VARIANT_CHANGE_EVENT =
    (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) || 'variant-change';
  var MOBILE_QUERY = '(min-width: 750px)';
  var COLOR_PATTERN = /colou?r/i;
  var SIZE_PATTERN = /size/i;

  /* ------------------------------------------------------------------ utils */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/([^\w-])/g, '\\$1');
  }

  function firstTextNode(element) {
    for (var i = 0; i < element.childNodes.length; i++) {
      var node = element.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== '') return node;
    }
    return null;
  }

  /* -------------------------------------------------- variant option labels */

  function optionNameFor(group) {
    var checked = group.querySelector('input:checked');
    if (checked) return { name: checked.dataset.optionName || '', value: checked.value };

    var select = group.querySelector('select');
    if (select) {
      var option = select.selectedOptions && select.selectedOptions[0];
      if (!option) option = select.querySelector('option[selected]');
      if (option) return { name: option.dataset.optionName || '', value: option.value };
    }

    return null;
  }

  function localizedLabel(name, config) {
    if (!name) return '';
    if (COLOR_PATTERN.test(name) && config.colorLabel) return config.colorLabel;
    if (SIZE_PATTERN.test(name) && config.sizeLabel) return config.sizeLabel;
    return name;
  }

  function decorateOptionLabels(wrapper) {
    var config = {
      enabled: wrapper.dataset.showSelectedName === 'true',
      colorLabel: wrapper.dataset.colorLabel || '',
      sizeLabel: wrapper.dataset.sizeLabel || '',
    };

    toArray(wrapper.querySelectorAll('.product-form__input')).forEach(function (group) {
      var label = group.querySelector('legend.form__label') || group.querySelector('label.form__label');
      if (!label) return;

      var selection = optionNameFor(group);
      if (!selection || !selection.name) return;

      group.setAttribute('data-option-name', selection.name);

      var display = localizedLabel(selection.name, config);
      // Dawn's swatch picker already prints the value in [data-selected-value].
      var dawnValue = label.querySelector('[data-selected-value]');
      var textNode = firstTextNode(label);

      if (dawnValue) {
        if (textNode) textNode.nodeValue = display + ': ';
        return;
      }

      if (!config.enabled) {
        if (textNode) textNode.nodeValue = display;
        return;
      }

      if (textNode) {
        textNode.nodeValue = display + ': ';
      } else {
        label.insertBefore(document.createTextNode(display + ': '), label.firstChild);
      }

      var valueEl = label.querySelector('[data-selected-value-name]');
      if (!valueEl) {
        valueEl = document.createElement('span');
        valueEl.className = 'product-custom__option-value';
        valueEl.setAttribute('data-selected-value-name', '');
        label.appendChild(valueEl);
      }
      valueEl.textContent = selection.value;
    });
  }

  /* --------------------------------------------------------- size guide modal */

  function SizeGuide(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-size-guide-trigger]');
    this.dialog = root.querySelector('[data-size-guide-dialog]');
    this.closeButton = root.querySelector('[data-size-guide-close]');
    this.lastFocused = null;

    if (!this.trigger || !this.dialog) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.trigger.addEventListener('click', this.open);
    if (this.closeButton) this.closeButton.addEventListener('click', this.close);
    this.dialog.addEventListener('cancel', this.close);
    this.dialog.addEventListener('close', this.afterClose.bind(this));
    this.dialog.addEventListener('click', this.onBackdropClick.bind(this));
    this.dialog.addEventListener('keydown', this.onKeydown);
  }

  SizeGuide.prototype.open = function (event) {
    if (event) event.preventDefault();
    this.lastFocused = document.activeElement;
    document.documentElement.classList.add('product-custom-modal-open');

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
      this.dialog.setAttribute('data-fallback-open', '');
      document.addEventListener('keydown', this.onKeydown);
    }

    var focusTarget = this.closeButton || this.dialog;
    if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
  };

  SizeGuide.prototype.close = function (event) {
    if (event) event.preventDefault();

    if (this.dialog.open && typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
      this.dialog.removeAttribute('data-fallback-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.afterClose();
    }
  };

  SizeGuide.prototype.afterClose = function () {
    this.dialog.removeAttribute('data-fallback-open');
    document.documentElement.classList.remove('product-custom-modal-open');
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    } else if (this.trigger) {
      this.trigger.focus();
    }
  };

  SizeGuide.prototype.onBackdropClick = function (event) {
    if (event.target === this.dialog) this.close();
  };

  SizeGuide.prototype.onKeydown = function (event) {
    if (event.key !== 'Escape') return;
    // Native <dialog> fires `cancel`; this covers the fallback path only.
    if (typeof this.dialog.showModal !== 'function') {
      event.preventDefault();
      this.close();
    }
  };

  /** Moves the trigger next to the size option label when one exists. */
  SizeGuide.prototype.relocate = function (scope) {
    if (!this.trigger || !scope) return;

    var groups = toArray(scope.querySelectorAll('.product-form__input'));
    for (var i = 0; i < groups.length; i++) {
      var name = groups[i].getAttribute('data-option-name') || '';
      if (!SIZE_PATTERN.test(name)) continue;

      var label = groups[i].querySelector('legend.form__label') || groups[i].querySelector('label.form__label');
      if (!label || label.contains(this.trigger)) return;

      label.appendChild(this.trigger);
      this.root.classList.add('product-custom__size-guide--relocated');
      return;
    }
  };

  /* ------------------------------------------------------------ sticky ATC bar */

  function StickyAtc(bar, productInfo) {
    this.bar = bar;
    this.productInfo = productInfo;
    this.button = bar.querySelector('[data-sticky-atc-button]');
    this.observer = null;
    this.visible = false;

    var targetId = bar.dataset.target;
    this.target = targetId ? productInfo.querySelector('#' + escapeSelector(targetId)) : null;
    if (!this.target) this.target = productInfo.querySelector('.product-form__submit');
    if (!this.target || typeof IntersectionObserver === 'undefined') return;

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0 });
    this.observer.observe(this.target);
  }

  StickyAtc.prototype.onIntersect = function (entries) {
    var entry = entries[0];
    if (!entry) return;
    // Only once the main button has been scrolled past (it sits above the viewport).
    this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
  };

  StickyAtc.prototype.toggle = function (show) {
    if (show === this.visible) return;
    this.visible = show;
    this.bar.classList.toggle('is-visible', show);

    if (show) {
      document.documentElement.style.setProperty('--sticky-atc-height', this.bar.offsetHeight + 'px');
      document.body.classList.add('has-sticky-atc');
    } else {
      document.documentElement.style.removeProperty('--sticky-atc-height');
      document.body.classList.remove('has-sticky-atc');
    }
  };

  StickyAtc.prototype.destroy = function () {
    if (this.observer) this.observer.disconnect();
    this.toggle(false);
  };

  StickyAtc.prototype.syncButton = function (sourceButton) {
    if (!this.button || !sourceButton) return;
    var disabled = sourceButton.hasAttribute('disabled');
    this.button.disabled = disabled;
    var sourceLabel = sourceButton.querySelector('span');
    var label = this.button.querySelector('span');
    if (sourceLabel && label) label.textContent = sourceLabel.textContent.trim();
  };

  /* ---------------------------------------------------------------- tabs */

  function ProductTabs(root) {
    this.root = root;
    this.tablist = root.querySelector('[data-tablist]');
    this.tabs = toArray(root.querySelectorAll('[role="tab"]'));
    this.triggers = toArray(root.querySelectorAll('[data-accordion-trigger]'));
    this.panels = toArray(root.querySelectorAll('[data-panel]'));

    if (!this.tabs.length || !this.panels.length) return;

    this.panels.forEach(function (panel) {
      panel.dataset.tabLabelledby = panel.getAttribute('aria-labelledby') || '';
    });

    this.active = this.tabs[0].dataset.tab;
    this.mq = window.matchMedia(MOBILE_QUERY);
    this.onModeChange = this.applyMode.bind(this);

    var self = this;

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        self.activate(tab.dataset.tab, true);
      });
    });

    if (this.tablist) this.tablist.addEventListener('keydown', this.onKeydown.bind(this));

    this.triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var key = trigger.dataset.accordionTrigger;
        if (self.mq.matches) {
          self.activate(key, true);
        } else {
          self.toggleAccordion(key);
        }
      });
    });

    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', this.onModeChange);
    } else if (typeof this.mq.addListener === 'function') {
      this.mq.addListener(this.onModeChange);
    }

    this.applyMode();
    root.setAttribute('data-tabs-ready', '');
  }

  ProductTabs.prototype.panelFor = function (key) {
    for (var i = 0; i < this.panels.length; i++) {
      if (this.panels[i].dataset.panel === key) return this.panels[i];
    }
    return null;
  };

  ProductTabs.prototype.triggerFor = function (key) {
    for (var i = 0; i < this.triggers.length; i++) {
      if (this.triggers[i].dataset.accordionTrigger === key) return this.triggers[i];
    }
    return null;
  };

  ProductTabs.prototype.activate = function (key, focusTab) {
    this.active = key;

    this.tabs.forEach(function (tab) {
      var selected = tab.dataset.tab === key;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.classList.toggle('is-active', selected);
      if (selected && focusTab) tab.focus();
    });

    var self = this;
    this.panels.forEach(function (panel) {
      var open = panel.dataset.panel === key;
      if (open) {
        panel.removeAttribute('data-panel-inactive');
      } else {
        panel.setAttribute('data-panel-inactive', '');
      }
      var trigger = self.triggerFor(panel.dataset.panel);
      if (trigger) {
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        trigger.classList.toggle('is-active', open);
      }
    });
  };

  ProductTabs.prototype.toggleAccordion = function (key) {
    var panel = this.panelFor(key);
    var trigger = this.triggerFor(key);
    if (!panel || !trigger) return;

    var willOpen = panel.hasAttribute('data-panel-inactive');
    if (willOpen) {
      panel.removeAttribute('data-panel-inactive');
    } else {
      panel.setAttribute('data-panel-inactive', '');
    }
    trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    trigger.classList.toggle('is-active', willOpen);
    if (willOpen) this.active = key;
  };

  ProductTabs.prototype.onKeydown = function (event) {
    var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (keys.indexOf(event.key) === -1) return;

    var current = this.tabs.indexOf(document.activeElement);
    if (current === -1) return;
    event.preventDefault();

    var next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;

    this.activate(this.tabs[next].dataset.tab, true);
  };

  ProductTabs.prototype.applyMode = function () {
    var desktop = this.mq.matches;
    var self = this;

    this.panels.forEach(function (panel) {
      if (desktop) {
        panel.setAttribute('role', 'tabpanel');
        if (panel.dataset.tabLabelledby) panel.setAttribute('aria-labelledby', panel.dataset.tabLabelledby);
        panel.setAttribute('tabindex', '0');
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('tabindex');
        var trigger = self.triggerFor(panel.dataset.panel);
        if (trigger && trigger.id) panel.setAttribute('aria-labelledby', trigger.id);
      }
    });

    // Desktop must always show exactly one panel; mobile keeps the current one open.
    this.activate(this.active, false);
  };

  /* ------------------------------------------------------------ controller */

  var currentSticky = null;

  function ProductCustom(productInfo) {
    this.el = productInfo;
    this.sectionId = productInfo.dataset.section;
    this.pickerWrapper = productInfo.querySelector('[data-product-custom-variant-picker]');
    this.stickyBar = productInfo.querySelector('[data-sticky-atc-bar]');

    var sizeGuideRoot = productInfo.querySelector('[data-size-guide]');
    this.sizeGuide = sizeGuideRoot ? new SizeGuide(sizeGuideRoot) : null;

    var tabsRoot = productInfo.querySelector('[data-product-tabs]');
    if (tabsRoot && !tabsRoot.hasAttribute('data-tabs-ready')) this.tabs = new ProductTabs(tabsRoot);

    if (this.stickyBar) {
      // Only ever one sticky bar on screen: tear down the one from a swapped-out product.
      if (currentSticky) currentSticky.destroy();
      this.sticky = new StickyAtc(this.stickyBar, productInfo);
      currentSticky = this.sticky;
    } else {
      this.sticky = null;
    }

    this.refreshPicker();
  }

  ProductCustom.prototype.refreshPicker = function () {
    if (this.pickerWrapper) decorateOptionLabels(this.pickerWrapper);
    if (this.sizeGuide) this.sizeGuide.relocate(this.pickerWrapper || this.el);
  };

  ProductCustom.prototype.updateFrom = function (html, sourceSectionId) {
    var self = this;
    var swap = function (prefix) {
      var source = html.getElementById(prefix + '-' + sourceSectionId);
      var destination = self.el.querySelector('#' + escapeSelector(prefix + '-' + self.sectionId));
      if (!source || !destination) return null;
      destination.innerHTML = source.innerHTML;
      destination.classList.toggle('hidden', source.classList.contains('hidden'));
      return source;
    };

    swap('DesignCode');
    swap('LowStock');
    swap('StickyAtcPrice');

    if (this.sticky) {
      this.sticky.syncButton(html.getElementById('ProductSubmitButton-' + sourceSectionId));
    }

    this.refreshPicker();
  };

  /* ------------------------------------------------------------------ boot */

  var instances = new WeakMap();

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = root.querySelectorAll ? toArray(root.querySelectorAll('product-info')) : [];
    if (root.matches && root.matches('product-info')) nodes.push(root);

    nodes.forEach(function (productInfo) {
      if (instances.has(productInfo)) return;
      instances.set(productInfo, new ProductCustom(productInfo));
    });
  }

  function instanceForSection(sectionId) {
    var el =
      document.querySelector('product-info[data-section="' + sectionId + '"]') ||
      document.querySelector('product-info[data-original-section="' + sectionId + '"]');
    if (!el) return null;
    if (!instances.has(el)) instances.set(el, new ProductCustom(el));
    return instances.get(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Dawn dispatches this whenever a <product-info> element connects, including
  // after a full product swap.
  document.addEventListener('product-info:loaded', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  if (typeof subscribe === 'function') {
    subscribe(VARIANT_CHANGE_EVENT, function (payload) {
      var data = payload && payload.data;
      if (!data || !data.html) return;
      var instance = instanceForSection(data.sectionId);
      if (instance) instance.updateFrom(data.html, data.sectionId);
    });
  }
})();
